import { ServiceArea } from '../../types/serviceArea';
import {
	LAYER_SEL_ADM0_FILL,
	LAYER_SEL_ADM0_LINE,
	LAYER_SEL_ADM1_FILL,
	LAYER_SEL_ADM1_LINE,
	LAYER_SEL_ADM2_FILL,
	LAYER_SEL_ADM2_LINE,
	LAYER_SEL_ADM3_FILL,
	LAYER_SEL_ADM3_LINE,
	LAYER_SEL_MARKERS_REGION,
	LAYER_STORED_FILL,
	SELECTION_FILTER_NONE,
	SOURCE_ADM0,
	SOURCE_ADM1,
	SOURCE_ADM2,
	SOURCE_ADM3,
	SOURCE_SEARCH_PREVIEW,
	SOURCE_SEL_MARKERS,
	SOURCE_STORED,
} from './progressiveGeoBoundariesMapConstants';

function selectionFilterForShapeIds(ids: string[]): any {
	if (!ids || ids.length === 0) {
		return ['==', 'shapeID', SELECTION_FILTER_NONE];
	}
	const shapeIdInExpression: any[] = ['in', 'shapeID'];
	for (let idIndex = 0; idIndex < ids.length; idIndex++) {
		shapeIdInExpression.push(ids[idIndex]);
	}
	return shapeIdInExpression;
}

function dedupeShapeIdStrings(ids: string[]): string[] {
	const seen: { [key: string]: boolean } = {};
	const out: string[] = [];
	for (let i = 0; i < ids.length; i++) {
		const id = ids[i];
		if (!id || seen[id]) {
			continue;
		}
		seen[id] = true;
		out.push(id);
	}
	return out;
}

function normalizeShapeGroupToken(raw: string): string {
	if (!raw) {
		return '';
	}
	return String(raw).trim().toUpperCase();
}

/**
 * True when ADM1 props' shapeGroup/shapeISO matches the country's boundaryShapeGroup (USA vs US).
 */
function adm1FeatureBelongsToCountryShapeGroup(countryArea: ServiceArea, adm1Props: any): boolean {
	if (!countryArea || !adm1Props) {
		return false;
	}
	const selected = normalizeShapeGroupToken(
		countryArea.boundaryShapeGroup ? String(countryArea.boundaryShapeGroup) : ''
	);
	if (!selected) {
		return false;
	}
	const fromFeature = normalizeShapeGroupToken(
		String(adm1Props.shapeGroup || adm1Props.shapeISO || '')
	);
	if (!fromFeature) {
		return false;
	}
	if (selected === fromFeature) {
		return true;
	}
	const selectedUs = selected === 'USA' || selected === 'US';
	const featureUs = fromFeature === 'USA' || fromFeature === 'US';
	return selectedUs && featureUs;
}

/**
 * ADM1 belongs to the country row when shapeGroup matches the parent, or bbox centroid lies in
 * country geometry (fallback). Used for implied highlights and for exploding whole-country → states.
 */
function adm1FeatureContainedInCountrySelection(
	countryParent: ServiceArea | null | undefined,
	countryGeometry: any,
	adm1Feature: any
): boolean {
	if (!adm1Feature || !adm1Feature.properties || !adm1Feature.geometry) {
		return false;
	}
	if (String(adm1Feature.properties.shapeType || '').toUpperCase() !== 'ADM1') {
		return false;
	}
	const hasShapeGroup =
		countryParent &&
		countryParent.boundaryShapeGroup &&
		String(countryParent.boundaryShapeGroup).trim().length > 0;
	if (
		hasShapeGroup &&
		adm1FeatureBelongsToCountryShapeGroup(countryParent, adm1Feature.properties)
	) {
		return true;
	}
	if (countryGeometry) {
		const bbox = bboxFromGeometry(adm1Feature.geometry);
		if (!bbox) {
			return false;
		}
		const cx = (bbox[0] + bbox[2]) / 2;
		const cy = (bbox[1] + bbox[3]) / 2;
		if (lngLatInGeometry(cx, cy, countryGeometry)) {
			return true;
		}
	}
	return false;
}

/**
 * Same as {@link adm1FeatureContainedInCountrySelection} for ADM2 (county) features vs a country parent.
 */
function adm2FeatureContainedInCountrySelection(
	countryParent: ServiceArea | null | undefined,
	countryGeometry: any,
	adm2Feature: any
): boolean {
	if (!adm2Feature || !adm2Feature.properties || !adm2Feature.geometry) {
		return false;
	}
	if (String(adm2Feature.properties.shapeType || '').toUpperCase() !== 'ADM2') {
		return false;
	}
	const hasShapeGroup =
		countryParent &&
		countryParent.boundaryShapeGroup &&
		String(countryParent.boundaryShapeGroup).trim().length > 0;
	if (
		hasShapeGroup &&
		adm1FeatureBelongsToCountryShapeGroup(countryParent, adm2Feature.properties)
	) {
		return true;
	}
	if (countryGeometry) {
		const bbox = bboxFromGeometry(adm2Feature.geometry);
		if (!bbox) {
			return false;
		}
		const cx = (bbox[0] + bbox[2]) / 2;
		const cy = (bbox[1] + bbox[3]) / 2;
		if (lngLatInGeometry(cx, cy, countryGeometry)) {
			return true;
		}
	}
	return false;
}

/**
 * On State tab with a whole-country row, highlight every loaded ADM1 in that country (purple).
 * Prefers shapeGroup / shapeISO match on features (geoBoundaries); falls back to bbox center in
 * country geometry so elongated coastal states (Florida, Atlantic seaboard) are not missed when
 * their bounding-box center lies outside the ADM0 polygon.
 */
function collectImpliedRegionShapeIdsInsideWholeCountries(
	map: any,
	serviceAreas: ServiceArea[],
	selectionFormMode: string
): string[] {
	const mode = selectionFormMode ? String(selectionFormMode) : '';
	if (mode !== 'region' || !map) {
		return [];
	}
	// Fast-path: no whole-country selections → no implied regions, skip the expensive ADM1 scan.
	let anyWholeCountrySelection = false;
	for (let areaScanIndex = 0; areaScanIndex < serviceAreas.length; areaScanIndex++) {
		const candidateCountry = serviceAreas[areaScanIndex];
		if (
			candidateCountry &&
			candidateCountry.placeType === 'country' &&
			candidateCountry.boundaryShapeId
		) {
			anyWholeCountrySelection = true;
			break;
		}
	}
	if (!anyWholeCountrySelection) {
		return [];
	}
	// Read SOURCE_ADM1 once and cache each feature's bbox so per-country iterations do not recompute it.
	const adm1Features = readGeoJsonSourceFeatures(map, SOURCE_ADM1);
	if (!adm1Features || adm1Features.length === 0) {
		return [];
	}
	const impliedRegionShapeIds: string[] = [];
	for (let selectionIndex = 0; selectionIndex < serviceAreas.length; selectionIndex++) {
		const selectedWholeCountry = serviceAreas[selectionIndex];
		if (
			!selectedWholeCountry ||
			selectedWholeCountry.placeType !== 'country' ||
			!selectedWholeCountry.boundaryShapeId
		) {
			continue;
		}
		const countryGeometry = resolveCountryGeometryFromArea(map, selectedWholeCountry, SOURCE_ADM0);
		const countryRowHasShapeGroup = !!(
			selectedWholeCountry.boundaryShapeGroup &&
			String(selectedWholeCountry.boundaryShapeGroup).trim().length > 0
		);
		if (!countryGeometry && !countryRowHasShapeGroup) {
			continue;
		}
		for (let adm1Index = 0; adm1Index < adm1Features.length; adm1Index++) {
			const adm1Feature = adm1Features[adm1Index];
			if (!adm1Feature || !adm1Feature.properties || !adm1Feature.geometry) {
				continue;
			}
			const regionShapeId = adm1Feature.properties.shapeID;
			if (regionShapeId === undefined || regionShapeId === null) {
				continue;
			}
			if (
				adm1FeatureContainedInCountrySelection(selectedWholeCountry, countryGeometry, adm1Feature)
			) {
				impliedRegionShapeIds.push(String(regionShapeId));
			}
		}
	}
	return dedupeShapeIdStrings(impliedRegionShapeIds);
}

/**
 * On County tab with a whole-state row, highlight every loaded ADM2 whose bbox center lies in that
 * state (green), while the state row still drives the purple state outline.
 */
function collectImpliedDistrictShapeIdsInsideWholeStates(
	map: any,
	serviceAreas: ServiceArea[],
	selectionFormMode: string
): string[] {
	const mode = selectionFormMode ? String(selectionFormMode) : '';
	if (mode !== 'district' || !map) {
		return [];
	}
	// Fast-path: no whole-state selections → no implied counties, avoid reading ADM2 entirely.
	// Without this, every county click with many state selections runs the O(states × ADM2) PIP below.
	let anyWholeStateSelection = false;
	for (let areaScanIndex = 0; areaScanIndex < serviceAreas.length; areaScanIndex++) {
		const candidateState = serviceAreas[areaScanIndex];
		if (
			candidateState &&
			candidateState.boundaryShapeId &&
			(candidateState.placeType === 'region' || candidateState.placeType === 'state')
		) {
			anyWholeStateSelection = true;
			break;
		}
	}
	if (!anyWholeStateSelection) {
		return [];
	}
	// Read SOURCE_ADM2 once and precompute each feature's bbox center + a per-feature bbox. The prior
	// implementation did `readGeoJsonSourceFeatures` and `bboxFromGeometry` inside the outer state
	// loop, so each of N state selections paid the bbox cost for every county — ~N × 3000 redundant
	// bbox walks and O(N × ADM2 × stateVerts) PIP tests. With 38 US states that was a 10 s stall.
	const adm2Features = readGeoJsonSourceFeatures(map, SOURCE_ADM2);
	if (!adm2Features || adm2Features.length === 0) {
		return [];
	}
	const adm2BoundingBoxCenters: {
		shapeId: string;
		shapeName: string;
		lng: number;
		lat: number;
		minLng: number;
		minLat: number;
		maxLng: number;
		maxLat: number;
		polygonSamplePoints: [number, number][];
		// Area-weighted centroid, but only set when it is confirmed inside the county's own polygon.
		// For highly concave counties (U-shapes) where the centroid falls in the "notch" we leave this
		// null and fall back to the vertex-majority sampling below.
		interiorCentroid: [number, number] | null;
	}[] = [];
	for (let adm2Index = 0; adm2Index < adm2Features.length; adm2Index++) {
		const adm2Feature = adm2Features[adm2Index];
		if (!adm2Feature || !adm2Feature.properties || !adm2Feature.geometry) {
			continue;
		}
		if (String(adm2Feature.properties.shapeType || '').toUpperCase() !== 'ADM2') {
			continue;
		}
		const districtShapeId = adm2Feature.properties.shapeID;
		if (districtShapeId === undefined || districtShapeId === null) {
			continue;
		}
		const districtBoundingBox = bboxFromGeometry(adm2Feature.geometry);
		if (!districtBoundingBox) {
			continue;
		}
		// Compute the area-weighted centroid once per county. For counties whose centroid falls
		// inside their own polygon (the overwhelmingly common case) we can use it as a single
		// authoritative "interior point" for state-containment — far more reliable than the bbox
		// center (which can land outside the polygon for L- or U-shaped counties like Shoshone, ID)
		// and than vertex sampling (which wobbles on shared state borders).
		const areaCentroid = areaWeightedCentroidFromGeometry(adm2Feature.geometry);
		const areaCentroidIsInsideOwnPolygon =
			areaCentroid !== null &&
			lngLatInGeometry(areaCentroid[0], areaCentroid[1], adm2Feature.geometry);
		adm2BoundingBoxCenters.push({
			shapeId: String(districtShapeId),
			shapeName: String(adm2Feature.properties.shapeName || ''),
			lng: (districtBoundingBox[0] + districtBoundingBox[2]) / 2,
			lat: (districtBoundingBox[1] + districtBoundingBox[3]) / 2,
			minLng: districtBoundingBox[0],
			minLat: districtBoundingBox[1],
			maxLng: districtBoundingBox[2],
			maxLat: districtBoundingBox[3],
			polygonSamplePoints: samplePolygonVerticesForContainmentTest(adm2Feature.geometry, 8),
			interiorCentroid: areaCentroidIsInsideOwnPolygon ? areaCentroid : null,
		});
	}
	const impliedDistrictShapeIds: string[] = [];
	for (let selectionIndex = 0; selectionIndex < serviceAreas.length; selectionIndex++) {
		const selectedWholeStateOrRegion = serviceAreas[selectionIndex];
		if (!selectedWholeStateOrRegion || !selectedWholeStateOrRegion.boundaryShapeId) {
			continue;
		}
		if (
			selectedWholeStateOrRegion.placeType !== 'region' &&
			selectedWholeStateOrRegion.placeType !== 'state'
		) {
			continue;
		}
		const stateGeometry = resolveStateGeometryFromArea(
			map,
			selectedWholeStateOrRegion,
			SOURCE_ADM1
		);
		if (!stateGeometry) {
			continue;
		}
		// Pre-compute the state bbox so counties whose bbox clearly doesn't overlap the state
		// short-circuit before the expensive ring PIP (the state's outer ring can be hundreds of verts).
		const stateBoundingBox = bboxFromGeometry(stateGeometry);
		for (let adm2CacheIndex = 0; adm2CacheIndex < adm2BoundingBoxCenters.length; adm2CacheIndex++) {
			const cachedCenter = adm2BoundingBoxCenters[adm2CacheIndex];
			if (stateBoundingBox) {
				if (
					cachedCenter.maxLng < stateBoundingBox[0] ||
					cachedCenter.minLng > stateBoundingBox[2] ||
					cachedCenter.maxLat < stateBoundingBox[1] ||
					cachedCenter.minLat > stateBoundingBox[3]
				) {
					continue;
				}
			}
			// Primary test: the county's own area-weighted centroid (already verified inside the
			// county's own polygon during precompute). If that point lies inside the state polygon,
			// the county is unambiguously in this state — this cleanly handles both the false
			// negative (Shoshone, ID: bbox center sits in Montana due to a distorted bbox, but the
			// true centroid is well inside Idaho) and the false positive (Beaverhead, MT: centroid
			// is deep inside Montana, rejected immediately — no border-vertex ambiguity).
			if (cachedCenter.interiorCentroid !== null) {
				if (
					lngLatInGeometry(
						cachedCenter.interiorCentroid[0],
						cachedCenter.interiorCentroid[1],
						stateGeometry
					)
				) {
					impliedDistrictShapeIds.push(cachedCenter.shapeId);
				}
				continue;
			}
			// Fallback: for the rare county whose area-weighted centroid falls in a non-convex
			// "notch" outside its own polygon, use vertex-majority sampling against the state.
			const districtPolygonSamples = cachedCenter.polygonSamplePoints;
			const totalSampleCount = districtPolygonSamples.length;
			if (totalSampleCount === 0) {
				continue;
			}
			const majorityThreshold = Math.ceil(totalSampleCount / 2);
			let districtSamplesInsideStateCount = 0;
			for (
				let districtSampleIndex = 0;
				districtSampleIndex < totalSampleCount;
				districtSampleIndex++
			) {
				const samplePoint = districtPolygonSamples[districtSampleIndex];
				if (lngLatInGeometry(samplePoint[0], samplePoint[1], stateGeometry)) {
					districtSamplesInsideStateCount++;
					if (districtSamplesInsideStateCount >= majorityThreshold) {
						break;
					}
				}
			}
			if (districtSamplesInsideStateCount >= majorityThreshold) {
				impliedDistrictShapeIds.push(cachedCenter.shapeId);
			}
		}
	}
	return dedupeShapeIdStrings(impliedDistrictShapeIds);
}

/** Draw ADM1 / ADM0 selection strokes above subdivision fills so outer borders stay visible. */
function raiseProgressiveSelectionOutlineLayers(map: any): void {
	if (!map || !map.getLayer || !map.moveLayer) {
		return;
	}
	const beforeId = map.getLayer(LAYER_SEL_MARKERS_REGION)
		? LAYER_SEL_MARKERS_REGION
		: map.getLayer(LAYER_STORED_FILL)
		? LAYER_STORED_FILL
		: null;
	if (!beforeId) {
		return;
	}
	try {
		if (map.getLayer(LAYER_SEL_ADM1_LINE)) {
			map.moveLayer(LAYER_SEL_ADM1_LINE, beforeId);
		}
		if (map.getLayer(LAYER_SEL_ADM0_LINE)) {
			map.moveLayer(LAYER_SEL_ADM0_LINE, beforeId);
		}
	} catch (_ignoredMoveLayerErr) {
		// ignore during teardown or if style is immutable
	}
}

function findFeatureGeometryByShapeIdFromSource(
	map: any,
	sourceId: string,
	shapeId: string
): any | null {
	if (!map || !shapeId) {
		return null;
	}
	const features = readGeoJsonSourceFeatures(map, sourceId);
	for (let featureIndex = 0; featureIndex < features.length; featureIndex++) {
		const feature = features[featureIndex];
		if (!feature || !feature.properties || !feature.geometry) {
			continue;
		}
		if (String(feature.properties.shapeID) === shapeId) {
			return feature.geometry;
		}
	}
	return null;
}

/**
 * Polygon / multipolygon for the search-result preview. Prefer geometry from loaded geoBoundaries
 * sources (real admin outline); only then fall back to `area.boundaries` so we never draw a
 * coarse Mapbox-style bbox rectangle when ADM1/ADM2 features are available.
 */
export function resolvePreviewBoundaryGeometry(map: any, area: ServiceArea): any | null {
	if (!area) {
		return null;
	}
	const shapeIdString = area.boundaryShapeId ? String(area.boundaryShapeId) : '';
	const placeTypeName = area.placeType ? String(area.placeType) : 'region';
	let geometryFromMap: any | null = null;
	if (shapeIdString) {
		if (placeTypeName === 'country') {
			geometryFromMap = resolveCountryGeometryFromArea(map, area, SOURCE_ADM0);
		} else if (placeTypeName === 'region' || placeTypeName === 'state') {
			geometryFromMap = resolveStateGeometryFromArea(map, area, SOURCE_ADM1);
		} else if (placeTypeName === 'district') {
			geometryFromMap = findFeatureGeometryByShapeIdFromSource(map, SOURCE_ADM2, shapeIdString);
		} else if (placeTypeName === 'place') {
			geometryFromMap = findFeatureGeometryByShapeIdFromSource(map, SOURCE_ADM3, shapeIdString);
		}
	}
	if (geometryFromMap) {
		return geometryFromMap;
	}
	if (area.boundaries && area.boundaries.type) {
		return area.boundaries;
	}
	return null;
}

function previewLineKeyFromPlaceType(placeType: string | undefined): string {
	const normalized = placeType ? String(placeType) : '';
	if (normalized === 'country') {
		return 'country';
	}
	if (normalized === 'region' || normalized === 'state') {
		return 'region';
	}
	if (normalized === 'place') {
		return 'place';
	}
	return 'district';
}

/**
 * Search dropdown preview: same outline palette as selection (country / state / county / place).
 * Pass null to clear.
 */
export function updateSearchPreviewHighlight(map: any, previewArea: ServiceArea | null): void {
	const previewSource = map && map.getSource ? map.getSource(SOURCE_SEARCH_PREVIEW) : null;
	if (!previewSource || previewSource.type !== 'geojson') {
		return;
	}
	if (!previewArea) {
		try {
			previewSource.setData(emptyFeatureCollection());
		} catch (_ignoredPreviewClearErr) {}
		return;
	}
	const previewGeometry = resolvePreviewBoundaryGeometry(map, previewArea);
	if (!previewGeometry) {
		try {
			previewSource.setData(emptyFeatureCollection());
		} catch (_ignoredPreviewEmptyErr) {}
		return;
	}
	const previewLineKey = previewLineKeyFromPlaceType(previewArea.placeType);
	const previewFeatureCollection = {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				properties: {
					name: previewArea.name || 'preview',
					previewLineKey: previewLineKey,
				},
				geometry: previewGeometry,
			},
		],
	};
	try {
		previewSource.setData(previewFeatureCollection);
	} catch (_ignoredPreviewSetErr) {}
}

/**
 * Highlights saved boundary picks by admin level and placeType (matches marker colors).
 * Selection layers have no minzoom so outlines stay visible at world zoom.
 */
export function updateGeoBoundariesSelectionHighlight(
	map: any,
	serviceAreas: ServiceArea[],
	options?: { showMarkers?: boolean; selectionFormMode?: string }
): void {
	const showMarkers = !options || options.showMarkers !== false;
	const selectionFormModeForHighlight =
		options && options.selectionFormMode != null && options.selectionFormMode !== ''
			? String(options.selectionFormMode)
			: 'district';
	const countryIds: string[] = [];
	const regionIds: string[] = [];
	const districtIds: string[] = [];
	const localityIds: string[] = [];
	for (let selectionIndex = 0; selectionIndex < serviceAreas.length; selectionIndex++) {
		const selectedArea = serviceAreas[selectionIndex];
		if (!selectedArea.boundaryShapeId) {
			continue;
		}
		const boundaryShapeIdString = String(selectedArea.boundaryShapeId);
		if (selectedArea.placeType === 'country') {
			countryIds.push(boundaryShapeIdString);
		} else if (selectedArea.placeType === 'region' || selectedArea.placeType === 'state') {
			regionIds.push(boundaryShapeIdString);
		} else if (selectedArea.placeType === 'district') {
			districtIds.push(boundaryShapeIdString);
		} else if (selectedArea.placeType === 'place') {
			localityIds.push(boundaryShapeIdString);
		}
	}
	const impliedRegionIds = collectImpliedRegionShapeIdsInsideWholeCountries(
		map,
		serviceAreas,
		selectionFormModeForHighlight
	);
	const impliedDistrictIds = collectImpliedDistrictShapeIdsInsideWholeStates(
		map,
		serviceAreas,
		selectionFormModeForHighlight
	);
	const regionIdsForFilter = dedupeShapeIdStrings(regionIds.concat(impliedRegionIds));
	const districtIdsForFilter = dedupeShapeIdStrings(districtIds.concat(impliedDistrictIds));
	const selectionLayerTriplets: [string, string, string[]][] = [
		[LAYER_SEL_ADM0_FILL, LAYER_SEL_ADM0_LINE, countryIds],
		[LAYER_SEL_ADM1_FILL, LAYER_SEL_ADM1_LINE, regionIdsForFilter],
		[LAYER_SEL_ADM2_FILL, LAYER_SEL_ADM2_LINE, districtIdsForFilter],
		[LAYER_SEL_ADM3_FILL, LAYER_SEL_ADM3_LINE, localityIds],
	];
	for (let tripletIndex = 0; tripletIndex < selectionLayerTriplets.length; tripletIndex++) {
		const selectionLayerFilter = selectionFilterForShapeIds(
			selectionLayerTriplets[tripletIndex][2]
		);
		if (map.getLayer(selectionLayerTriplets[tripletIndex][0])) {
			map.setFilter(selectionLayerTriplets[tripletIndex][0], selectionLayerFilter);
		}
		if (map.getLayer(selectionLayerTriplets[tripletIndex][1])) {
			map.setFilter(selectionLayerTriplets[tripletIndex][1], selectionLayerFilter);
		}
	}
	raiseProgressiveSelectionOutlineLayers(map);

	const storedSource = map.getSource(SOURCE_STORED);
	if (storedSource) {
		const storedFeatures: any[] = [];
		for (let storedIndex = 0; storedIndex < serviceAreas.length; storedIndex++) {
			const areaWithStoredGeometry = serviceAreas[storedIndex];
			if (areaWithStoredGeometry.boundaries) {
				// Areas tied to geoBoundaries `shapeID` are already drawn from SOURCE_ADM0/1/2/3 via
				// LAYER_SEL_* filters. Pushing the same `boundaries` geometry into SOURCE_STORED stacks a
				// second fill+line (seams, moiré, boxy inner edges) — including search → postcode → county
				// picks where `resolveSearchAreaToFinestBoundary` sets both `boundaryShapeId` and
				// `boundaries` from the same hit. Keep SOURCE_STORED only when there is no progressive
				// tier (e.g. some `postcode` / `poi` rows) or no shape id to filter on.
				const placeTypeForStored = areaWithStoredGeometry.placeType || 'region';
				const hasProgressiveShapeHighlight =
					(placeTypeForStored === 'country' ||
						placeTypeForStored === 'region' ||
						placeTypeForStored === 'state' ||
						placeTypeForStored === 'district' ||
						placeTypeForStored === 'place') &&
					areaWithStoredGeometry.boundaryShapeId !== undefined &&
					areaWithStoredGeometry.boundaryShapeId !== null &&
					String(areaWithStoredGeometry.boundaryShapeId) !== '';
				if (hasProgressiveShapeHighlight) {
					continue;
				}
				let adminShapeTypeForMap = 'ADM1';
				if (placeTypeForStored === 'country') {
					adminShapeTypeForMap = 'ADM0';
				} else if (placeTypeForStored === 'region' || placeTypeForStored === 'state') {
					adminShapeTypeForMap = 'ADM1';
				} else if (placeTypeForStored === 'district') {
					adminShapeTypeForMap = 'ADM2';
				} else if (placeTypeForStored === 'place') {
					adminShapeTypeForMap = 'ADM3';
				}
				const props: Record<string, string> = {
					placeType: placeTypeForStored,
					name: areaWithStoredGeometry.name || '',
					shapeType: adminShapeTypeForMap,
					shapeName: areaWithStoredGeometry.name || '',
				};
				if (
					areaWithStoredGeometry.boundaryShapeId !== undefined &&
					areaWithStoredGeometry.boundaryShapeId !== null
				) {
					props.shapeID = String(areaWithStoredGeometry.boundaryShapeId);
				}
				storedFeatures.push({
					type: 'Feature',
					geometry: areaWithStoredGeometry.boundaries,
					properties: props,
				});
			}
		}
		storedSource.setData({ type: 'FeatureCollection', features: storedFeatures });
	}

	const markerSource = map.getSource(SOURCE_SEL_MARKERS);
	if (markerSource) {
		const markerFeatures: any[] = [];
		if (showMarkers) {
			for (let markerIndex = 0; markerIndex < serviceAreas.length; markerIndex++) {
				const markerSourceArea = serviceAreas[markerIndex];
				if (
					markerSourceArea.placeType !== 'region' &&
					markerSourceArea.placeType !== 'state' &&
					markerSourceArea.placeType !== 'district'
				) {
					continue;
				}
				if (
					!markerSourceArea.center ||
					(markerSourceArea.center[0] === 0 && markerSourceArea.center[1] === 0)
				) {
					continue;
				}
				markerFeatures.push({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [markerSourceArea.center[0], markerSourceArea.center[1]],
					},
					properties: { placeType: markerSourceArea.placeType, name: markerSourceArea.name },
				});
			}
		}
		markerSource.setData({ type: 'FeatureCollection', features: markerFeatures });
	}
}

export const emptyFeatureCollection = (): any => ({ type: 'FeatureCollection', features: [] });

export function isFiniteScreenNumber(value: any): boolean {
	return typeof value === 'number' && !isNaN(value) && value !== Infinity && value !== -Infinity;
}

/** Non-zero canvas — required before project() / queryRenderedFeatures make sense. */
export function hasRenderableMapCanvas(map: any): boolean {
	if (!map || !map.getCanvas) {
		return false;
	}
	const canvas = map.getCanvas();
	return !!canvas && canvas.clientWidth >= 1 && canvas.clientHeight >= 1;
}

/**
 * For pointer hit-tests: need a real canvas; try/catch around queryRenderedFeatures handles the rest.
 * Do not require map.loaded() / isStyleLoaded() here — progressive attach runs inside `load`, and those
 * flags can be false synchronously even though the map is usable, which blocked ADM1/ADM2 loading entirely.
 */
export function isMapReadyForRenderedFeatureQueries(map: any): boolean {
	return hasRenderableMapCanvas(map);
}

/** Screen point for map center, or null if the map cannot project yet. */
export function projectMapCenterToScreen(map: any): { x: number; y: number } | null {
	if (!hasRenderableMapCanvas(map)) {
		return null;
	}
	let projectedCenterPoint: any;
	try {
		projectedCenterPoint = map.project(map.getCenter());
	} catch (_err) {
		return null;
	}
	if (
		!projectedCenterPoint ||
		!isFiniteScreenNumber(projectedCenterPoint.x) ||
		!isFiniteScreenNumber(projectedCenterPoint.y)
	) {
		return null;
	}
	return { x: projectedCenterPoint.x, y: projectedCenterPoint.y };
}

/**
 * Area-weighted polygon centroid (Green's theorem) for the outer ring of a Polygon, or the
 * area-weighted combination across sub-polygons for a MultiPolygon. This is guaranteed inside
 * convex polygons and inside most real-world county shapes, so it's a much more reliable
 * "interior point" than the bbox centroid for containment tests.
 */
function areaWeightedCentroidFromGeometry(geom: any): [number, number] | null {
	if (!geom || !geom.type || !geom.coordinates) {
		return null;
	}
	function accumulateRingSignedAreaAndCentroid(
		ring: number[][]
	): { signedArea: number; cx: number; cy: number } | null {
		if (!ring || ring.length < 3) {
			return null;
		}
		let signedAreaDoubled = 0;
		let cxAccum = 0;
		let cyAccum = 0;
		for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
			const x0 = ring[vertexIndex][0];
			const y0 = ring[vertexIndex][1];
			const x1 = ring[vertexIndex + 1][0];
			const y1 = ring[vertexIndex + 1][1];
			const cross = x0 * y1 - x1 * y0;
			signedAreaDoubled += cross;
			cxAccum += (x0 + x1) * cross;
			cyAccum += (y0 + y1) * cross;
		}
		const signedArea = signedAreaDoubled * 0.5;
		if (signedArea === 0) {
			return null;
		}
		return {
			signedArea: signedArea,
			cx: cxAccum / (3 * signedAreaDoubled),
			cy: cyAccum / (3 * signedAreaDoubled),
		};
	}
	if (geom.type === 'Polygon') {
		const outerRing = geom.coordinates && geom.coordinates[0];
		const ringCentroid = accumulateRingSignedAreaAndCentroid(outerRing);
		if (!ringCentroid) {
			return null;
		}
		return [ringCentroid.cx, ringCentroid.cy];
	}
	if (geom.type === 'MultiPolygon') {
		let totalAreaAbs = 0;
		let cxAreaWeighted = 0;
		let cyAreaWeighted = 0;
		for (let polyIndex = 0; polyIndex < geom.coordinates.length; polyIndex++) {
			const subPoly = geom.coordinates[polyIndex];
			const outerRing = subPoly && subPoly[0];
			const ringCentroid = accumulateRingSignedAreaAndCentroid(outerRing);
			if (!ringCentroid) {
				continue;
			}
			const absArea = Math.abs(ringCentroid.signedArea);
			totalAreaAbs += absArea;
			cxAreaWeighted += ringCentroid.cx * absArea;
			cyAreaWeighted += ringCentroid.cy * absArea;
		}
		if (totalAreaAbs === 0) {
			return null;
		}
		return [cxAreaWeighted / totalAreaAbs, cyAreaWeighted / totalAreaAbs];
	}
	return null;
}

/**
 * Evenly-spaced vertex samples from a polygon's outer ring (or largest sub-polygon's outer ring for
 * MultiPolygon). These points lie on the real county outline, so they beat bbox-based samples for
 * county-in-state containment tests: geoBoundaries sometimes gives a feature a bbox much larger than
 * its actual polygon (e.g. Shoshone, ID spans ~1.4° across a 1.1° bbox that extends into Montana),
 * so synthetic bbox offsets can land entirely outside the county's real shape. Vertex samples don't
 * have that failure mode — they are guaranteed on the polygon boundary.
 */
function samplePolygonVerticesForContainmentTest(
	geom: any,
	sampleCount: number
): [number, number][] {
	if (!geom || !geom.type || !geom.coordinates) {
		return [];
	}
	function sampleFromRing(ring: number[][]): [number, number][] {
		if (!ring || ring.length === 0) {
			return [];
		}
		if (ring.length <= sampleCount) {
			const all: [number, number][] = [];
			for (let i = 0; i < ring.length; i++) {
				if (ring[i] && ring[i].length >= 2) {
					all.push([ring[i][0], ring[i][1]]);
				}
			}
			return all;
		}
		const step = ring.length / sampleCount;
		const samples: [number, number][] = [];
		for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
			const vertexIndex = Math.floor(sampleIndex * step);
			const vertex = ring[vertexIndex];
			if (vertex && vertex.length >= 2) {
				samples.push([vertex[0], vertex[1]]);
			}
		}
		return samples;
	}
	if (geom.type === 'Polygon') {
		const outerRing = geom.coordinates && geom.coordinates[0];
		return sampleFromRing(outerRing);
	}
	if (geom.type === 'MultiPolygon') {
		// Sample from each sub-polygon's outer ring; caps total samples at sampleCount × subPolygonCount.
		const multiPolySamples: [number, number][] = [];
		for (let polyIndex = 0; polyIndex < geom.coordinates.length; polyIndex++) {
			const subPoly = geom.coordinates[polyIndex];
			const outerRing = subPoly && subPoly[0];
			const subSamples = sampleFromRing(outerRing);
			for (let subSampleIndex = 0; subSampleIndex < subSamples.length; subSampleIndex++) {
				multiPolySamples.push(subSamples[subSampleIndex]);
			}
		}
		return multiPolySamples;
	}
	return [];
}

function bboxFromGeometry(geom: any): [number, number, number, number] | null {
	if (!geom || !geom.coordinates) {
		return null;
	}
	let minLng = 180;
	let minLat = 90;
	let maxLng = -180;
	let maxLat = -90;
	function consumeRing(ring: number[][]) {
		for (let ringVertexIndex = 0; ringVertexIndex < ring.length; ringVertexIndex++) {
			const lng = ring[ringVertexIndex][0];
			const lat = ring[ringVertexIndex][1];
			minLng = Math.min(minLng, lng);
			maxLng = Math.max(maxLng, lng);
			minLat = Math.min(minLat, lat);
			maxLat = Math.max(maxLat, lat);
		}
	}
	function walkCoords(coords: any, _nestedDepth: number) {
		if (!coords || coords.length === 0) {
			return;
		}
		if (typeof coords[0][0] === 'number') {
			consumeRing(coords as number[][]);
			return;
		}
		for (let coordBranchIndex = 0; coordBranchIndex < coords.length; coordBranchIndex++) {
			walkCoords(coords[coordBranchIndex], _nestedDepth + 1);
		}
	}
	if (geom.type === 'Polygon') {
		walkCoords(geom.coordinates, 0);
	} else if (geom.type === 'MultiPolygon') {
		walkCoords(geom.coordinates, 0);
	} else {
		return null;
	}
	if (minLng > maxLng || minLat > maxLat) {
		return null;
	}
	return [minLng, minLat, maxLng, maxLat];
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
	if (!ring || ring.length < 3) {
		return false;
	}
	let inside = false;
	const vertexCount = ring.length;
	for (
		let vertexIndex = 0, prevVertexIndex = vertexCount - 1;
		vertexIndex < vertexCount;
		prevVertexIndex = vertexIndex++
	) {
		const currentVertexLng = ring[vertexIndex][0];
		const currentVertexLat = ring[vertexIndex][1];
		const previousVertexLng = ring[prevVertexIndex][0];
		const previousVertexLat = ring[prevVertexIndex][1];
		const edgeDeltaLat = previousVertexLat - currentVertexLat;
		if (edgeDeltaLat === 0) {
			continue;
		}
		const intersect =
			currentVertexLat > lat !== previousVertexLat > lat &&
			lng <
				((previousVertexLng - currentVertexLng) * (lat - currentVertexLat)) / edgeDeltaLat +
					currentVertexLng;
		if (intersect) {
			inside = !inside;
		}
	}
	return inside;
}

function pointInPolygonCoords(lng: number, lat: number, coords: number[][][]): boolean {
	if (!coords || coords.length === 0) {
		return false;
	}
	const outerRing = coords[0];
	if (!pointInRing(lng, lat, outerRing)) {
		return false;
	}
	for (let innerRingIndex = 1; innerRingIndex < coords.length; innerRingIndex++) {
		if (pointInRing(lng, lat, coords[innerRingIndex])) {
			return false;
		}
	}
	return true;
}

export function lngLatInGeometry(lng: number, lat: number, geom: any): boolean {
	if (!geom || !geom.type) {
		return false;
	}
	if (geom.type === 'Polygon') {
		return pointInPolygonCoords(lng, lat, geom.coordinates);
	}
	if (geom.type === 'MultiPolygon') {
		const polygonRingsList = geom.coordinates;
		for (let polygonIndex = 0; polygonIndex < polygonRingsList.length; polygonIndex++) {
			if (pointInPolygonCoords(lng, lat, polygonRingsList[polygonIndex])) {
				return true;
			}
		}
	}
	return false;
}

/** When queryRenderedFeatures misses (opacity, GPU limits), derive ISO3 from loaded ADM0 features. */
export function resolveIsoFromAdm0FeatureList(
	lng: number,
	lat: number,
	adm0Features: any[]
): string | null {
	if (!adm0Features || adm0Features.length === 0) {
		return null;
	}
	for (let featureIndex = 0; featureIndex < adm0Features.length; featureIndex++) {
		const adm0Feature = adm0Features[featureIndex];
		if (!adm0Feature || !adm0Feature.geometry || !adm0Feature.properties) {
			continue;
		}
		const geometryBbox = bboxFromGeometry(adm0Feature.geometry);
		if (geometryBbox) {
			if (
				lng < geometryBbox[0] ||
				lng > geometryBbox[2] ||
				lat < geometryBbox[1] ||
				lat > geometryBbox[3]
			) {
				continue;
			}
		}
		if (lngLatInGeometry(lng, lat, adm0Feature.geometry)) {
			const shapeGroupOrIso = adm0Feature.properties.shapeGroup || adm0Feature.properties.shapeISO;
			if (shapeGroupOrIso) {
				return String(shapeGroupOrIso);
			}
		}
	}
	return null;
}

/**
 * Reads GeoJSON features from a Mapbox GL GeoJSON source (internal `_data` / serialize fallback).
 */
export function readGeoJsonSourceFeatures(map: any, sourceId: string): any[] {
	if (!map || !map.getSource) {
		return [];
	}
	try {
		const geoJsonSource = map.getSource(sourceId);
		if (!geoJsonSource || geoJsonSource.type !== 'geojson') {
			return [];
		}
		const internalData = geoJsonSource._data;
		if (
			internalData &&
			internalData.type === 'FeatureCollection' &&
			Array.isArray(internalData.features)
		) {
			return internalData.features;
		}
		if (typeof geoJsonSource.serialize === 'function') {
			const serialized = geoJsonSource.serialize();
			if (serialized && serialized.type === 'FeatureCollection' && serialized.features) {
				return serialized.features;
			}
		}
		return [];
	} catch (_err) {
		return [];
	}
}
export function boundaryFeatureToServiceArea(geoJsonFeature: any): ServiceArea {
	const featureProps = geoJsonFeature.properties || {};
	const adminShapeType = featureProps.shapeType;
	let placeType = 'region';
	if (adminShapeType === 'ADM0') {
		placeType = 'country';
	} else if (adminShapeType === 'ADM2') {
		placeType = 'district';
	} else if (adminShapeType === 'ADM3') {
		placeType = 'place';
	}
	const geometryBbox = bboxFromGeometry(geoJsonFeature.geometry);
	const center: [number, number] = geometryBbox
		? [(geometryBbox[0] + geometryBbox[2]) / 2, (geometryBbox[1] + geometryBbox[3]) / 2]
		: [0, 0];
	const shapeIdRaw = featureProps.shapeID;
	const shapeGroup = featureProps.shapeGroup || featureProps.shapeISO;
	return {
		name: featureProps.shapeName || 'Unknown',
		placeType,
		center,
		bbox: geometryBbox,
		boundaryShapeId:
			shapeIdRaw !== undefined && shapeIdRaw !== null ? String(shapeIdRaw) : undefined,
		boundaryShapeGroup: shapeGroup ? String(shapeGroup) : undefined,
		boundaries: geoJsonFeature.geometry || undefined,
	};
}

/**
 * Uses saved geometry when present, otherwise finds the ADM1 feature on the map by `boundaryShapeId`.
 */
export function resolveStateGeometryFromArea(
	map: any,
	stateArea: ServiceArea,
	adm1SourceId: string
): any | null {
	if (stateArea.boundaries && stateArea.boundaries.type) {
		return stateArea.boundaries;
	}
	if (!stateArea.boundaryShapeId) {
		return null;
	}
	const targetBoundaryShapeId = String(stateArea.boundaryShapeId);
	const adm1Features = readGeoJsonSourceFeatures(map, adm1SourceId);
	for (let featureIndex = 0; featureIndex < adm1Features.length; featureIndex++) {
		const feature = adm1Features[featureIndex];
		if (!feature || !feature.properties || !feature.geometry) {
			continue;
		}
		if (String(feature.properties.shapeID) === targetBoundaryShapeId) {
			return feature.geometry;
		}
	}
	return null;
}

/**
 * Uses saved geometry when present, otherwise finds the ADM0 feature on the map by `boundaryShapeId`.
 */
export function resolveCountryGeometryFromArea(
	map: any,
	countryArea: ServiceArea,
	adm0SourceId: string
): any | null {
	if (countryArea.boundaries && countryArea.boundaries.type) {
		return countryArea.boundaries;
	}
	if (!countryArea.boundaryShapeId) {
		return null;
	}
	const targetBoundaryShapeId = String(countryArea.boundaryShapeId);
	const adm0Features = readGeoJsonSourceFeatures(map, adm0SourceId);
	for (let featureIndex = 0; featureIndex < adm0Features.length; featureIndex++) {
		const feature = adm0Features[featureIndex];
		if (!feature || !feature.properties || !feature.geometry) {
			continue;
		}
		if (String(feature.properties.shapeID) === targetBoundaryShapeId) {
			return feature.geometry;
		}
	}
	return null;
}

/**
 * All ADM2 districts whose bbox center lies inside `stateGeometry`, excluding one shape id.
 */
export function buildDistrictServiceAreasInStateExcluding(
	adm2Features: any[],
	stateGeometry: any,
	excludeBoundaryShapeId: string
): ServiceArea[] {
	const districtServiceAreas: ServiceArea[] = [];
	if (!adm2Features || !stateGeometry || !excludeBoundaryShapeId) {
		return districtServiceAreas;
	}
	for (let featureIndex = 0; featureIndex < adm2Features.length; featureIndex++) {
		const adm2Feature = adm2Features[featureIndex];
		if (!adm2Feature || !adm2Feature.properties || !adm2Feature.geometry) {
			continue;
		}
		if (String(adm2Feature.properties.shapeType || '').toUpperCase() !== 'ADM2') {
			continue;
		}
		const districtShapeId = adm2Feature.properties.shapeID;
		if (districtShapeId === undefined || districtShapeId === null) {
			continue;
		}
		if (String(districtShapeId) === excludeBoundaryShapeId) {
			continue;
		}
		const districtBoundingBox = bboxFromGeometry(adm2Feature.geometry);
		if (!districtBoundingBox) {
			continue;
		}
		const bboxCenterLng = (districtBoundingBox[0] + districtBoundingBox[2]) / 2;
		const bboxCenterLat = (districtBoundingBox[1] + districtBoundingBox[3]) / 2;
		if (!lngLatInGeometry(bboxCenterLng, bboxCenterLat, stateGeometry)) {
			continue;
		}
		districtServiceAreas.push(boundaryFeatureToServiceArea(adm2Feature));
	}
	return districtServiceAreas;
}

/**
 * ADM1 regions in the country (shapeGroup on parent when present, else bbox centroid in geometry),
 * excluding one shape id. Used when refining whole-country → all states except the clicked one.
 */
export function buildRegionServiceAreasInCountryExcluding(
	adm1Features: any[],
	countryGeometry: any,
	excludeBoundaryShapeId: string,
	countryParentArea?: ServiceArea | null
): ServiceArea[] {
	const regionServiceAreas: ServiceArea[] = [];
	if (!adm1Features || !excludeBoundaryShapeId) {
		return regionServiceAreas;
	}
	const hasGeom = countryGeometry && countryGeometry.type;
	const hasGroup =
		countryParentArea &&
		countryParentArea.boundaryShapeGroup &&
		String(countryParentArea.boundaryShapeGroup).trim().length > 0;
	if (!hasGeom && !hasGroup) {
		return regionServiceAreas;
	}
	for (let featureIndex = 0; featureIndex < adm1Features.length; featureIndex++) {
		const adm1Feature = adm1Features[featureIndex];
		if (!adm1Feature || !adm1Feature.properties || !adm1Feature.geometry) {
			continue;
		}
		if (String(adm1Feature.properties.shapeType || '').toUpperCase() !== 'ADM1') {
			continue;
		}
		const regionShapeId = adm1Feature.properties.shapeID;
		if (regionShapeId === undefined || regionShapeId === null) {
			continue;
		}
		if (String(regionShapeId) === excludeBoundaryShapeId) {
			continue;
		}
		if (
			!adm1FeatureContainedInCountrySelection(
				countryParentArea || null,
				countryGeometry,
				adm1Feature
			)
		) {
			continue;
		}
		regionServiceAreas.push(boundaryFeatureToServiceArea(adm1Feature));
	}
	return regionServiceAreas;
}

/**
 * All ADM2 districts in the country (shapeGroup vs parent when present, else bbox centroid in geometry),
 * excluding one shape id.
 */
export function buildDistrictServiceAreasInCountryExcluding(
	adm2Features: any[],
	countryGeometry: any,
	excludeBoundaryShapeId: string,
	countryParentArea?: ServiceArea | null
): ServiceArea[] {
	const districtServiceAreas: ServiceArea[] = [];
	if (!adm2Features || !excludeBoundaryShapeId) {
		return districtServiceAreas;
	}
	const hasGeom = countryGeometry && countryGeometry.type;
	const hasGroup =
		countryParentArea &&
		countryParentArea.boundaryShapeGroup &&
		String(countryParentArea.boundaryShapeGroup).trim().length > 0;
	if (!hasGeom && !hasGroup) {
		return districtServiceAreas;
	}
	for (let featureIndex = 0; featureIndex < adm2Features.length; featureIndex++) {
		const adm2Feature = adm2Features[featureIndex];
		if (!adm2Feature || !adm2Feature.properties || !adm2Feature.geometry) {
			continue;
		}
		if (String(adm2Feature.properties.shapeType || '').toUpperCase() !== 'ADM2') {
			continue;
		}
		const districtShapeId = adm2Feature.properties.shapeID;
		if (districtShapeId === undefined || districtShapeId === null) {
			continue;
		}
		if (String(districtShapeId) === excludeBoundaryShapeId) {
			continue;
		}
		if (
			!adm2FeatureContainedInCountrySelection(
				countryParentArea || null,
				countryGeometry,
				adm2Feature
			)
		) {
			continue;
		}
		districtServiceAreas.push(boundaryFeatureToServiceArea(adm2Feature));
	}
	return districtServiceAreas;
}

export function enrichDistrictServiceAreasWithLabels(
	districts: ServiceArea[],
	stateName: string,
	countryName: string
): ServiceArea[] {
	const stateNameSuffix = stateName || '';
	const countryNameSuffix = countryName || '';
	return districts.map((district) => {
		const districtBaseName = district.name || 'Unknown';
		return Object.assign({}, district, {
			name: districtBaseName + ', ' + stateNameSuffix + ', ' + countryNameSuffix,
		});
	});
}

/**
 * State (ADM1) name whose polygon contains the district bbox center — for labeling counties after
 * exploding a whole-country selection.
 */
export function resolveStateLabelForDistrictGeometry(
	districtGeometry: any,
	adm1Features: any[]
): string {
	if (!districtGeometry || !adm1Features || adm1Features.length === 0) {
		return '';
	}
	const districtBoundingBox = bboxFromGeometry(districtGeometry);
	if (!districtBoundingBox) {
		return '';
	}
	const bboxCenterLng = (districtBoundingBox[0] + districtBoundingBox[2]) / 2;
	const bboxCenterLat = (districtBoundingBox[1] + districtBoundingBox[3]) / 2;
	for (let featureIndex = 0; featureIndex < adm1Features.length; featureIndex++) {
		const adm1Feature = adm1Features[featureIndex];
		if (!adm1Feature || !adm1Feature.geometry || !adm1Feature.properties) {
			continue;
		}
		if (String(adm1Feature.properties.shapeType || '').toUpperCase() !== 'ADM1') {
			continue;
		}
		if (lngLatInGeometry(bboxCenterLng, bboxCenterLat, adm1Feature.geometry)) {
			const stateShapeName = adm1Feature.properties.shapeName;
			return stateShapeName ? String(stateShapeName) : '';
		}
	}
	return '';
}

export function enrichDistrictServiceAreasWithStateAndCountryLabels(
	districts: ServiceArea[],
	adm1Features: any[],
	countryName: string
): ServiceArea[] {
	const countryNameSuffix = countryName || '';
	return districts.map((district) => {
		const districtGeometry = district.boundaries;
		const stateName = districtGeometry
			? resolveStateLabelForDistrictGeometry(districtGeometry, adm1Features)
			: '';
		const districtBaseName = district.name || 'Unknown';
		const nameParts = [districtBaseName];
		if (stateName) {
			nameParts.push(stateName);
		}
		if (countryNameSuffix) {
			nameParts.push(countryNameSuffix);
		}
		return Object.assign({}, district, {
			name: nameParts.join(', '),
		});
	});
}

/**
 * Interior point for a district polygon: area-weighted centroid when it falls inside its own
 * polygon (handles concave counties like Shoshone ID where the bbox center sits outside).
 * Falls back to the bbox-based `area.center` when no district geometry is available.
 */
function resolveDistrictInteriorPoint(
	districtGeometry: any,
	area: ServiceArea
): [number, number] | null {
	if (districtGeometry) {
		const centroid = areaWeightedCentroidFromGeometry(districtGeometry);
		if (centroid && lngLatInGeometry(centroid[0], centroid[1], districtGeometry)) {
			return centroid;
		}
	}
	if (area.center && area.center.length === 2 && (area.center[0] !== 0 || area.center[1] !== 0)) {
		return [area.center[0], area.center[1]];
	}
	return null;
}

export interface ResolveParentShapeIdsCachedSources {
	adm0Features?: any[];
	adm1Features?: any[];
	adm2Features?: any[];
}

/**
 * Resolve parent admin shape ids from loaded ADM0/ADM1 GeoJSON sources on the map.
 *
 * Contract (matches the backend `supplier_facility_service_area` columns):
 *   - country   → `{ parentCountryShapeId: null, parentStateShapeId: null }`
 *   - region    → `{ parentCountryShapeId: <ADM0.shapeID>, parentStateShapeId: null }`
 *   - district  → `{ parentCountryShapeId: <ADM0.shapeID>, parentStateShapeId: <ADM1.shapeID> }`
 *
 * Country lookup keys on `boundaryShapeGroup` (ISO3, e.g. `DEU`).
 * For districts, the ADM1 lookup uses the district's own geometry (preferring `area.boundaries`,
 * otherwise the ADM2 source feature by `boundaryShapeId`) and picks the state whose polygon
 * contains the area-weighted centroid — the bbox-center shortcut is wrong for concave counties.
 * Returns `undefined` on either field when the relevant source isn't loaded yet — callers should
 * treat that as "leave previous value", not "set to null".
 *
 * For batch calls, pass `cachedSources` to avoid re-reading ADM0/ADM1/ADM2 per area.
 */
export function resolveParentShapeIdsForArea(
	map: any,
	area: ServiceArea,
	cachedSources?: ResolveParentShapeIdsCachedSources
): { parentCountryShapeId?: string | null; parentStateShapeId?: string | null } {
	if (!area) {
		return {};
	}
	const placeType = area.placeType ? String(area.placeType) : '';
	if (placeType === 'country') {
		return { parentCountryShapeId: null, parentStateShapeId: null };
	}
	if (placeType !== 'region' && placeType !== 'state' && placeType !== 'district') {
		return {};
	}

	const countryIsoOrGroup = area.boundaryShapeGroup
		? String(area.boundaryShapeGroup).trim().toUpperCase()
		: '';

	let parentCountryShapeId: string | null | undefined;
	if (countryIsoOrGroup) {
		const adm0Features =
			cachedSources && cachedSources.adm0Features
				? cachedSources.adm0Features
				: readGeoJsonSourceFeatures(map, SOURCE_ADM0);
		for (let featureIndex = 0; featureIndex < adm0Features.length; featureIndex++) {
			const adm0Feature = adm0Features[featureIndex];
			if (!adm0Feature || !adm0Feature.properties) {
				continue;
			}
			const featureGroup = String(
				adm0Feature.properties.shapeGroup || adm0Feature.properties.shapeISO || ''
			)
				.trim()
				.toUpperCase();
			if (featureGroup && featureGroup === countryIsoOrGroup) {
				const shapeIdRaw = adm0Feature.properties.shapeID;
				if (shapeIdRaw !== undefined && shapeIdRaw !== null) {
					parentCountryShapeId = String(shapeIdRaw);
				}
				break;
			}
		}
	}

	if (placeType === 'region' || placeType === 'state') {
		return { parentCountryShapeId, parentStateShapeId: null };
	}

	// placeType === 'district' — prefer the district's real polygon over its bbox center.
	let districtGeometry: any = area.boundaries || null;
	if (!districtGeometry && area.boundaryShapeId) {
		const adm2Features =
			cachedSources && cachedSources.adm2Features
				? cachedSources.adm2Features
				: readGeoJsonSourceFeatures(map, SOURCE_ADM2);
		const districtShapeIdString = String(area.boundaryShapeId);
		for (let featureIndex = 0; featureIndex < adm2Features.length; featureIndex++) {
			const candidate = adm2Features[featureIndex];
			if (
				candidate &&
				candidate.properties &&
				String(candidate.properties.shapeID) === districtShapeIdString
			) {
				districtGeometry = candidate.geometry;
				break;
			}
		}
	}
	const districtInteriorPoint = resolveDistrictInteriorPoint(districtGeometry, area);

	let parentStateShapeId: string | null | undefined;
	if (districtInteriorPoint) {
		const adm1Features =
			cachedSources && cachedSources.adm1Features
				? cachedSources.adm1Features
				: readGeoJsonSourceFeatures(map, SOURCE_ADM1);
		for (let featureIndex = 0; featureIndex < adm1Features.length; featureIndex++) {
			const adm1Feature = adm1Features[featureIndex];
			if (!adm1Feature || !adm1Feature.properties || !adm1Feature.geometry) {
				continue;
			}
			if (String(adm1Feature.properties.shapeType || '').toUpperCase() !== 'ADM1') {
				continue;
			}
			if (countryIsoOrGroup) {
				const featureGroup = String(
					adm1Feature.properties.shapeGroup || adm1Feature.properties.shapeISO || ''
				)
					.trim()
					.toUpperCase();
				if (featureGroup && featureGroup !== countryIsoOrGroup) {
					continue;
				}
			}
			if (
				lngLatInGeometry(districtInteriorPoint[0], districtInteriorPoint[1], adm1Feature.geometry)
			) {
				const shapeIdRaw = adm1Feature.properties.shapeID;
				if (shapeIdRaw !== undefined && shapeIdRaw !== null) {
					parentStateShapeId = String(shapeIdRaw);
				}
				break;
			}
		}
	}

	return { parentCountryShapeId, parentStateShapeId };
}

export function enrichRegionServiceAreasWithCountryLabel(
	regions: ServiceArea[],
	countryName: string
): ServiceArea[] {
	const countryNameSuffix = countryName || '';
	return regions.map((region) => {
		const regionBaseName = region.name || 'Unknown';
		return Object.assign({}, region, {
			name: countryNameSuffix ? regionBaseName + ', ' + countryNameSuffix : regionBaseName,
		});
	});
}
